import Subject from 'common/src/utils/pubsub/subject';
import {ZodTypeAny} from 'zod';
import CLLocalStorage from '../../localStorage/CLLocalStorage';
import {LS_KEY__CLINCH_COPILOT_CONTAINER_SETTINGS} from '../../localStorage/CLLocalStorage.consts';
import {FeatureFlagKey} from '../../featureFlags/featureFlags.types';
import isFlagEnabled from '../../featureFlags/utils/isFlagEnabled';
import {CLRef} from '../shared.types';
import {UIToolsKey} from './bootstrap/bootstrap.enums';
import {copilotContainerSettingsDefaults} from './copilot.config';
import {AgentName, ChatType} from './copilot.enums';
import {
  Agent,
  Chat,
  ChatConfig,
  CopilotContainerSettings,
  CopilotFile,
  Event,
  Tool,
} from './copilot.types';
import createChat from './utils/chat';

// trying to restrict access to these "private" variables
const allAgents: Agent[] = [];
let generalAgent: Agent | null = null;
let supportAgent: Agent | null = null;
const allChats: CLRef<Chat[]> = {current: []};
const eventsLogByChatId: Record<string, Event[]> = {};
const maxEventsLogPerChatSize = 2000;

// tools that are dynamically added/removed depending on mounted/unmounted react components. Ideally, there would be no UI tools - tools would only be defined on the agents and work directly through redux.
const __DEPRECATED_uiTools: Partial<Record<UIToolsKey, Tool<ZodTypeAny>[]>> = {};

// The copilot container settings: coordinates, dimensions, dropzone etc
const copilotContainerSettingsSubject = new Subject<CopilotContainerSettings>(
  CLLocalStorage.getParsed(LS_KEY__CLINCH_COPILOT_CONTAINER_SETTINGS) ||
    copilotContainerSettingsDefaults,
);

// Perhaps this function should not be public -
// instead there should only be chat-level service functions for getting and setting chat-specific data
function getChatById(chatId: string): Chat | undefined {
  const chat = allChats.current.find((chat) => chat.id === chatId);

  if (!chat) {
    console.error(`Chat with id "${chatId}" not found.`);
  }

  return chat;
}

function addChat(chat: Chat) {
  allChats.current.push(chat);
}

function setGeneralAgent(agent: Agent) {
  generalAgent = agent;
}

// delete this when skills mode is fully implemented and legacy mode is removed
function _generalChatConfigInLegacyMode(): ChatConfig {
  const copilotAgent = allAgents.find((agent) => agent.name === AgentName.Copilot);

  if (!copilotAgent) {
    throw Error(`${AgentName.Copilot} agent not found. Register it before creating a chat.`);
  }

  return {
    type: ChatType.General,
    agents: allAgents,
    fallbackAgent: copilotAgent,
    isPlanModeEnabled: true,
  };
}

function chatConfigForChatType(type: ChatType): ChatConfig {
  if (type === ChatType.Support) {
    if (!supportAgent) {
      throw Error('Support agent not set. Call setSupportAgent() before creating a support chat.');
    }

    return {
      type: ChatType.Support,
      agents: [supportAgent],
      fallbackAgent: supportAgent,
      isPlanModeEnabled: false,
    };
  }

  // delete this when skills mode is fully implemented and legacy mode is removed
  if (!isFlagEnabled(FeatureFlagKey.EnableCopilotSkills)) {
    return _generalChatConfigInLegacyMode();
  }

  if (!generalAgent) {
    throw Error('General agent not set. Call setGeneralAgent() before creating a chat.');
  }

  return {
    type: ChatType.General,
    agents: [generalAgent],
    fallbackAgent: generalAgent,
    isPlanModeEnabled: true,
  };
}

function createGeneralChat(): Chat {
  return createChat(chatConfigForChatType(ChatType.General));
}

function setSupportAgent(agent: Agent) {
  supportAgent = agent;
}

function createSupportChat(): Chat {
  return createChat(chatConfigForChatType(ChatType.Support));
}

function deleteChatById(chatId: string) {
  const chat = getChatById(chatId);

  if (!chat) {
    return;
  }

  chat.abortController?.abort();
  allChats.current = allChats.current.filter((chat) => chat.id !== chatId);
  delete eventsLogByChatId[chatId];
}

function clearChatById(chatId: string) {
  const chat = getChatById(chatId);

  if (!chat) {
    return;
  }

  const emptyChat = createChat({
    type: chat.type,
    agents: chat.agents,
    fallbackAgent: chat.fallbackAgent,
    isPlanModeEnabled: chat.isPlanModeEnabled,
  });

  emptyChat.id = chatId;
  allChats.current = allChats.current.map((chat) => (chat.id === chatId ? emptyChat : chat));
  delete eventsLogByChatId[chatId];
}

function registerAgent(agent: Agent) {
  if (allAgents.find((registeredAgent) => registeredAgent.name === agent.name)) {
    throw new Error(
      `Agent with name "${agent.name}" is already registered. Each agent must have a unique name.`,
    );
  }

  allAgents.push(agent);
}

function addFilesToChat(chatId: string, newFiles: CopilotFile[]) {
  const chat = getChatById(chatId);

  if (chat) {
    chat.files = [...chat.files, ...newFiles];
  }
}

function addToEventsLog(event: Event) {
  if (!eventsLogByChatId[event.chatId]) {
    eventsLogByChatId[event.chatId] = [];
  }

  const log = eventsLogByChatId[event.chatId];
  log.push(event);
  const excessCount = log.length - maxEventsLogPerChatSize;
  log.splice(0, excessCount);
}

function getEventsLog(chatId: string) {
  return [...(eventsLogByChatId[chatId] ?? [])];
}

export * as skills from './skills';
export * as tools from './tools';
export * as uiContexts from './uiContexts';
export {
  __DEPRECATED_uiTools,
  addChat,
  addFilesToChat,
  addToEventsLog,
  chatConfigForChatType,
  allChats,
  clearChatById,
  copilotContainerSettingsSubject,
  createGeneralChat,
  createSupportChat,
  deleteChatById,
  getChatById,
  getEventsLog,
  registerAgent,
  setGeneralAgent,
  setSupportAgent,
};
